/**
 * Browsable list of every notification this location has received — read,
 * unread, and expired alike.
 *
 * Clicking a row opens NotificationDetailModal on top (z-[60] over this
 * modal's z-50), with this panel dimmed behind it per the stacked-modal
 * pattern used by TransferFundsModal.
 */

import { useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useNotifications, type HistoryNotification } from '../hooks/useNotifications';
import { severityStyle, formatNotificationDate } from './notificationStyles';
import NotificationDetailModal from './NotificationDetailModal';

interface NotificationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationHistoryModal({
  isOpen,
  onClose,
}: NotificationHistoryModalProps) {
  const { history, unreadCount, loading, markRead, markAllRead } = useNotifications();
  const [selected, setSelected] = useState<HistoryNotification | null>(null);

  if (!isOpen) return null;

  const detailOpen = selected !== null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        <div
          className={`relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col ${
            detailOpen ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {history.length === 0 ? (
              <div className="py-16 px-4 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  {loading ? 'Loading…' : 'No notifications yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {history.map((n) => {
                  const style = severityStyle(n.severity);
                  const isUnread = !n.is_read && !n.is_expired;

                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => setSelected(n)}
                      className={`w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 ${
                        isUnread ? 'bg-indigo-50/40' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                            isUnread ? style.dot : 'border border-gray-300'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-sm truncate ${
                              n.is_expired
                                ? 'text-gray-400'
                                : isUnread
                                  ? 'font-semibold text-gray-900'
                                  : 'text-gray-600'
                            }`}
                          >
                            {n.title}
                          </p>
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">
                              {formatNotificationDate(n.created_at)}
                            </span>
                            <span
                              className={`text-xs flex-shrink-0 ${
                                n.is_expired
                                  ? 'italic text-gray-400'
                                  : isUnread
                                    ? 'font-medium text-indigo-700'
                                    : 'text-gray-400'
                              }`}
                            >
                              {n.is_expired ? 'Expired' : isUnread ? 'Unread' : 'Read'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 flex items-center justify-between gap-3 p-4 border-t border-gray-200">
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Mark all read ({unreadCount})
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      <NotificationDetailModal
        notification={selected}
        isOpen={detailOpen}
        onClose={() => setSelected(null)}
        onMarkRead={(id) => void markRead(id)}
      />
    </>
  );
}
